import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { getSnapshot } from './snapshots';
import { ClientAreaSchema, NotAssessedSchema, vendorIntelligenceKeys } from './vendor-intelligence';

/**
 * The vendor intelligence report (T-0268, T-0270; E14-design §4, §5;
 * contract v2 in E24, T-0277/T-0283). Routes in
 * `apps/api/src/routes/vendor-intelligence-report.ts`; shapes from the
 * report contract (`packages/vendor-fit/src/report-contract.ts`), mirrored
 * here as the web mirrors every API.
 *
 *   - The read carries the draft (pages of blocks, with tokens and
 *     citations; the cover facts), its token values and code tables, the
 *     published versions with their PDFs, and what the caller may do.
 *   - Prepare report drafts on the request path and answers the new read.
 *   - Rewrite answers a proposal and saves nothing; save and revert answer
 *     the block, which replaces the cached one before the read refreshes
 *     the prose issues.
 *   - Publish takes an Idempotency-Key.
 *   - A published version's frozen body is read through the snapshot route
 *     and parsed with `PublishedReportBodySchema`.
 *
 * Contract version 2 (ADR-026 decisions 5 and 6): the draft carries
 * `pages` (cover, summary, told_us, requirements, matrix, ranking,
 * `vendor:<id>`, set_aside, method), each with the slots its blocks fill
 * (a role and the heading printed over it); the block kinds `headline`,
 * `vendor_card` and `scenarios` join `paragraph` and `bullets`; a citation
 * carries the record id it prints as (`display`) and, on a list block, the
 * lines it supports; the cover facts carry the shortlist, the dispositions,
 * the planned go-live and the assessed count; the tables gain the
 * requirements by area, the area matrix, the ranking, the checks across
 * every vendor, the set-aside table, the sources and the needs table. Every
 * v2 field is optional here, so a draft prepared under version 1 (sections)
 * still reads.
 *
 * Every number is the server's (@caliber-2/vendor-fit); the web computes none.
 */

export const BLOCK_ORIGINS = [
  'ai_drafted',
  'template',
  'consultant_edited',
  'ai_rewritten',
] as const;

const RATING_ENUM = z.enum(['strong', 'partial', 'gap', 'unknown']);

export const BLOCK_KINDS = [
  'paragraph',
  'bullets',
  'headline',
  'vendor_card',
  'scenarios',
] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

export const ReportCitationSchema = z.object({
  key: z.string(),
  kind: z.string(),
  ref_id: z.string(),
  code: z.string().nullable(),
  /** The record id the text prints it as (v2): "VK-00966", "RTM 39", an interview's code and date. */
  display: z.string().nullable().optional(),
  /** For a bullets or scenarios block: the 1-based lines this citation supports; absent: the whole block. */
  lines: z.array(z.number()).optional(),
  /** A rating citation: the codes of the items it rests on. */
  rating_items: z.array(z.string()).optional(),
  label: z.string(),
  excerpt: z.string().nullable(),
  statement: z.string().nullable(),
  source_label: z.string(),
  date: z.string().nullable(),
  url: z.string().nullable(),
  locator_label: z.string().nullable(),
  confidential: z.boolean(),
  product_id: z.string().nullable(),
  module_id: z.string().nullable(),
});
export type ReportCitation = z.infer<typeof ReportCitationSchema>;

const ProseIssueSchema = z.object({ kind: z.string(), text: z.string() });
export type ReportProseIssue = z.infer<typeof ProseIssueSchema>;

export const ReportBlockSchema = z.object({
  id: z.string(),
  /** The page (v2) the block belongs to; `section_key` is the v1 field, kept one release. */
  page_key: z.string().optional(),
  section_key: z.string().optional(),
  position: z.number(),
  product_id: z.string().nullable(),
  kind: z.enum(BLOCK_KINDS),
  /** The slot the block fills on its page (v2): `verdict`, `why`, `watch_outs`, `scenarios`, ... */
  role: z.string().nullable().optional(),
  ai_text: z.string(),
  text: z.string(),
  display_text: z.string(),
  origin: z.enum(BLOCK_ORIGINS),
  citations: z.array(ReportCitationSchema),
  is_safe_refusal: z.boolean(),
  issues: z.array(ProseIssueSchema),
  last_instruction: z.string().nullable(),
  edited_by_user_id: z.string().nullable(),
  edited_at: z.string().nullable(),
});
export type ReportBlock = z.infer<typeof ReportBlockSchema>;

export const ReportSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  body: z.string(),
  tables: z.array(z.string()),
  blocks: z.array(ReportBlockSchema),
});
export type ReportSection = z.infer<typeof ReportSectionSchema>;

/** A slot a page's blocks fill (v2): the kind, the role, and the heading the page prints over it. */
export const ReportBlockSlotSchema = z.object({
  kind: z.enum(BLOCK_KINDS),
  role: z.string(),
  label: z.string().nullable(),
  per: z.string().optional(),
  template: z.boolean().optional(),
});
export type ReportBlockSlot = z.infer<typeof ReportBlockSlotSchema>;

/** One page of a v2 draft (E24-design §5): `cover`, `summary`, ..., `vendor:<id>`, `set_aside`, `method`. */
export const ReportPageSchema = z.object({
  key: z.string(),
  title: z.string(),
  body: z.string().optional(),
  tables: z.array(z.string()).optional(),
  /** The shortlisted vendor a `vendor:<id>` page is about. */
  product_id: z.string().nullable().optional(),
  slots: z.array(ReportBlockSlotSchema).optional(),
  blocks: z.array(ReportBlockSchema),
});
export type ReportPage = z.infer<typeof ReportPageSchema>;

/** The client-area roll-up frozen with the draft (E24-design §4): the same `ClientArea` the live evaluation serves. */
export type ClientArea = z.infer<typeof ClientAreaSchema>;

const DispositionSchema = z.object({
  product_id: z.string(),
  disposition: z.string(),
  reason: z.string().nullable().optional(),
  decided_by: z.object({ user_id: z.string(), name: z.string().nullable() }).nullable().optional(),
  decided_at: z.string().nullable().optional(),
});
export type ReportDisposition = z.infer<typeof DispositionSchema>;

/** The cover facts of a v2 draft or frozen body (ADR-026 decision 8). */
export const ReportCoverSchema = z.object({
  title: z.string().optional(),
  client_name: z.string().nullable().optional(),
  engagement_name: z.string().optional(),
  version: z.number().optional(),
  focus: z.string().nullable().optional(),
  drafted_at: z.string().optional(),
  /** When published; the draft's last change on an unpublished render. */
  published_at: z.string().nullable().optional(),
  /** True on a draft: the DRAFT banner. */
  draft: z.boolean().optional(),
  /** The shortlisted vendors in rank order: the first tile. */
  shortlist: z
    .array(
      z.object({
        rank: z.number().nullable(),
        product_id: z.string(),
        name: z.string(),
        index: z.number(),
        coverage: z.number(),
        no_evidence: z.number(),
      }),
    )
    .optional(),
  assessed_count: z.number().optional(),
  products_considered: z.number().optional(),
  modules_evaluated: z.number().optional(),
  requirements_weighted: z.number().optional(),
  requirements_critical: z.number().optional(),
  requirements_desired: z.number().optional(),
  /** `projects.planned_end` as an ISO date; the go-live tile is omitted when null. */
  planned_end: z.string().nullable().optional(),
  dispositions: z.array(DispositionSchema).optional(),
});
export type ReportCover = z.infer<typeof ReportCoverSchema>;

const UserRefSchema = z.object({ user_id: z.string(), name: z.string().nullable() });

/**
 * The other ranked products with the same index; tied products share a
 * rank (1, 2, 2, 4). Absent from a draft frozen before ties were reported:
 * read as no tie.
 */
const TiedWithSchema = z.array(z.string()).optional();

const FitProductSchema = z.object({
  product_id: z.string(),
  name: z.string(),
  platform_type: z.string().nullable().optional(),
  confirmed_items: z.number().optional(),
  index: z.number(),
  coverage: z.number(),
  rank: z.number().nullable(),
  tied_with: TiedWithSchema,
  top: z.boolean().optional(),
});

export const ReportFitSchema = z.object({
  modules: z.array(z.object({ module_id: z.string(), name: z.string() })),
  products: z.array(FitProductSchema),
  total_weight: z.number(),
  top: z.array(z.string()).optional(),
  requirements: z
    .object({
      critical: z.number(),
      desired: z.number(),
      not_set: z.number(),
      weighted: z.number(),
    })
    .optional(),
  not_assessed: NotAssessedSchema.optional(),
  /** The client-area roll-up (v2); absent from a draft frozen before it. */
  areas: z.array(ClientAreaSchema).optional(),
  /** The confirmed dispositions (v2); absent under version 1. */
  dispositions: z.array(DispositionSchema).optional(),
  planned_end: z.string().nullable().optional(),
  discovery: z
    .object({
      interviews: z.number(),
      from: z.string().nullable().optional(),
      to: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type ReportFit = z.infer<typeof ReportFitSchema>;

export const ReportDraftSchema = z.object({
  report_id: z.string(),
  status: z.literal('draft'),
  version: z.number(),
  focus: z.string().nullable(),
  drafted_by: UserRefSchema,
  drafted_at: z.string(),
  updated_at: z.string(),
  fit_result: ReportFitSchema,
  /** v1: the nine sections. A v2 draft carries `pages` instead; one of the two is present. */
  sections: z.array(ReportSectionSchema).optional(),
  pages: z.array(ReportPageSchema).optional(),
  cover: ReportCoverSchema.nullable().optional(),
  refusals: z.number(),
});
export type ReportDraft = z.infer<typeof ReportDraftSchema>;

const RATING = RATING_ENUM;

export const ComparisonTableSchema = z.object({
  modules: z.array(
    z.object({ module_id: z.string(), key: z.string(), name: z.string(), weight: z.number() }),
  ),
  products: z.array(
    z.object({
      product_id: z.string(),
      name: z.string(),
      rank: z.number(),
      tied_with: TiedWithSchema,
      index: z.number(),
      coverage: z.number(),
    }),
  ),
  cells: z.array(
    z.object({ product_id: z.string(), module_id: z.string(), rating: RATING.nullable() }),
  ),
});
export type ComparisonTable = z.infer<typeof ComparisonTableSchema>;

const ProductRefSchema = z.object({ product_id: z.string(), name: z.string() });

/** Page 4: the requirements by client area, with a total row. */
export const RequirementsByAreaSchema = z.object({
  rows: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      critical: z.number(),
      desired: z.number(),
      weight: z.number(),
      unmapped: z.number(),
    }),
  ),
  totals: z.object({
    critical: z.number(),
    desired: z.number(),
    weight: z.number(),
    unmapped: z.number(),
  }),
  areas: z.number(),
});
export type RequirementsByAreaTable = z.infer<typeof RequirementsByAreaSchema>;

/** Page 5: client areas by the vendors assessed in depth (shortlisted and alternates, at most eight). */
export const AreaMatrixTableSchema = z.object({
  areas: z.array(z.object({ key: z.string(), name: z.string(), weight: z.number() })),
  products: z.array(
    z.object({
      product_id: z.string(),
      name: z.string(),
      rank: z.number().nullable(),
      index: z.number(),
      coverage: z.number(),
      /** 100 minus coverage, one decimal: "weight with no evidence". */
      no_evidence: z.number().optional(),
      disposition: z.string().nullable().optional(),
    }),
  ),
  cells: z.array(
    z.object({
      area_key: z.string(),
      product_id: z.string(),
      rating: RATING.nullable(),
      value: z.number().optional(),
      unknown_share: z.number().optional(),
    }),
  ),
  total_weight: z.number().optional(),
  /** The formula sentence under the matrix, written by code. */
  note: z.string().optional(),
  /** Vendors in depth beyond the eighth column, left out of the matrix. */
  left_out: z.array(ProductRefSchema).optional(),
});
export type AreaMatrixTable = z.infer<typeof AreaMatrixTableSchema>;

/** Page 6: one bar per ranked vendor. */
export const RankingTableSchema = z.object({
  bars: z.array(
    z.object({
      product_id: z.string(),
      name: z.string(),
      index: z.number(),
      rank: z.number(),
      /** "2=" for a tied rank, else "2". */
      rank_label: z.string(),
      shortlisted: z.boolean(),
      disposition: z.string().nullable().optional(),
    }),
  ),
});
export type RankingTable = z.infer<typeof RankingTableSchema>;

/** Page 6: the checks across every assessed vendor, the base order first. */
export const SensitivityByVendorSchema = z.object({
  products: z.array(
    z.object({ product_id: z.string(), name: z.string(), shortlisted: z.boolean().optional() }),
  ),
  rows: z.array(
    z.object({
      /** `base` for the row "As scored", else the deriver's case key. */
      key: z.string(),
      label: z.string(),
      /** One per column, in `products` order; null when the vendor is unranked under the case. */
      indexes: z.array(z.number().nullable()),
      top: z.array(ProductRefSchema),
      /** `base` for the first row; else `holds`, `tied`, `changes`, `not_applicable`. */
      outcome: z.string(),
      detail: z.string().optional(),
    }),
  ),
  /** "The top three hold in 3 of 5 checks; 1 only ties." */
  summary: z.string().optional(),
});
export type SensitivityByVendor = z.infer<typeof SensitivityByVendorSchema>;

/** Page n-1: alternates and set-aside vendors with their reasons, then the not-assessed grouped by platform type. */
export const SetAsideTableSchema = z.object({
  /** The sentence over the table, written by code. */
  note: z.string().optional(),
  rows: z.array(
    z.object({
      product_id: z.string(),
      name: z.string().optional(),
      rank: z.number().nullable().optional(),
      index: z.number().nullable().optional(),
      coverage: z.number().optional(),
      disposition: z.string(),
      reason: z.string().nullable().optional(),
    }),
  ),
  not_assessed: z
    .array(
      z.object({
        platform_type: z.string().nullable().optional(),
        products: z.array(ProductRefSchema),
      }),
    )
    .optional(),
  assessed: z.number().optional(),
  considered: z.number().optional(),
});
export type SetAsideTable = z.infer<typeof SetAsideTableSchema>;

/** The method page's sources: items per vendor as read at draft time. */
export const SourcesTableSchema = z.object({
  products: z
    .array(z.object({ product_id: z.string(), name: z.string(), items: z.number().nullable() }))
    .optional(),
  read_at: z.string().nullable().optional(),
  discovery: z
    .object({
      interviews: z.number(),
      from: z.string().nullable().optional(),
      to: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type SourcesTable = z.infer<typeof SourcesTableSchema>;

/** Page 3: a need from Discovery, traced to the RTM and to each shortlisted vendor's evidence (E24-design §7). */
export const NeedsTableSchema = z.object({
  rows: z.array(
    z.object({
      pain_point_id: z.string(),
      number: z.string().nullable().optional(),
      need: z.string(),
      severity: z.string().nullable().optional(),
      heard: z
        .object({
          text: z.string(),
          interview_code: z.string().nullable().optional(),
          interview_title: z.string().nullable().optional(),
          held_at: z.string().nullable().optional(),
          /** "Core Finance · 13 Oct": the interview and date, as printed. */
          display: z.string(),
        })
        .nullable(),
      more_evidence: z.number(),
      rtm: z.array(z.object({ number: z.string(), display: z.string() })),
      evidence_by_vendor: z.array(
        z.object({
          product_id: z.string(),
          items: z.array(z.object({ item_id: z.string().optional(), code: z.string() })),
        }),
      ),
    }),
  ),
  /** Confirmed pain points with evidence linked to requirements. */
  pain_points: z.number(),
  /** The omission sentence when the page is left out; null when it prints. */
  note: z.string().nullable().optional(),
});
export type NeedsTable = z.infer<typeof NeedsTableSchema>;

export const ReportTablesSchema = z.object({
  comparison: ComparisonTableSchema.optional(),
  /** Requirements with no module, frozen with the draft (24 Oct 2026); absent from older drafts. */
  not_assessed: NotAssessedSchema.extend({ note: z.string() }).optional(),
  requirements_by_area: RequirementsByAreaSchema.nullable().optional(),
  area_matrix: AreaMatrixTableSchema.nullable().optional(),
  ranking: RankingTableSchema.nullable().optional(),
  sensitivity_by_vendor: SensitivityByVendorSchema.nullable().optional(),
  set_aside: SetAsideTableSchema.nullable().optional(),
  sources: SourcesTableSchema.nullable().optional(),
  needs: NeedsTableSchema.nullable().optional(),
});
export type ReportTables = z.infer<typeof ReportTablesSchema>;

export const ReportVersionSchema = z.object({
  report_id: z.string(),
  version: z.number(),
  snapshot_id: z.string(),
  published_at: z.string(),
  published_by: UserRefSchema,
  pdf: z
    .object({
      pdf_report_id: z.string(),
      /** 'rendering', 'succeeded' or 'failed'; any other state of the row reads as rendering. */
      render_status: z.string(),
      file_path: z.string(),
    })
    .nullable(),
  /** The firm's evidence export (xlsx) of the version (v2). */
  evidence_path: z.string().optional(),
});
export type ReportVersion = z.infer<typeof ReportVersionSchema>;

export const VendorReportSchema = z.object({
  engagement_id: z.string(),
  draft: ReportDraftSchema.nullable(),
  tokens: z.record(z.string()),
  tables: ReportTablesSchema.nullable(),
  versions: z.array(ReportVersionSchema),
  can_edit: z.boolean(),
  can_publish: z.boolean(),
  draft_blocked: z.object({ reason: z.string(), message: z.string() }).nullable(),
  stale: z.boolean(),
});
export type VendorReport = z.infer<typeof VendorReportSchema>;

const PublishResultSchema = z.object({
  published: ReportVersionSchema,
  draft_report_id: z.string(),
});
export type PublishVendorReportResult = z.infer<typeof PublishResultSchema>;

export const REWRITE_PRESET_KEYS = ['shorter', 'more_formal', 'plainer', 'add_a_point'] as const;
export type RewriteBlockRequest =
  | { preset: (typeof REWRITE_PRESET_KEYS)[number] }
  | { instruction: string };

const RewriteResultSchema = z.object({
  proposal: z
    .object({
      text: z.string(),
      display_text: z.string(),
      citations: z.array(ReportCitationSchema),
    })
    .nullable(),
  issues: z.array(z.object({ kind: z.string(), text: z.string(), message: z.string() })),
  instruction: z.string(),
});
export type RewriteBlockResult = z.infer<typeof RewriteResultSchema>;

export interface SaveBlockRequest {
  text: string;
  origin: 'consultant_edited' | 'ai_rewritten';
  instruction?: string;
  citation_keys?: string[];
}

const SaveResultSchema = z.object({
  block: ReportBlockSchema,
  issues: z.array(ProseIssueSchema),
});
export type SaveBlockResult = z.infer<typeof SaveResultSchema>;

// ---------------------------------------------------------------------------
// A published version's frozen body (ADR-009: write-once), read through the
// snapshot route the version names. Read-only on screen (T-0283).
// ---------------------------------------------------------------------------

const FrozenBlockSchema = z.object({
  id: z.string(),
  position: z.number().optional(),
  product_id: z.string().nullable().optional(),
  kind: z.enum(BLOCK_KINDS),
  role: z.string().nullable().optional(),
  text: z.string(),
  origin: z.enum(BLOCK_ORIGINS).optional(),
  citation_keys: z.array(z.string()).optional(),
  /** A list block: the citation keys each line carries, in line order. */
  line_citation_keys: z.array(z.array(z.string())).optional(),
});
export type FrozenReportBlock = z.infer<typeof FrozenBlockSchema>;

const FrozenPageSchema = z.object({
  key: z.string(),
  title: z.string(),
  body: z.string().optional(),
  tables: z.array(z.string()).optional(),
  product_id: z.string().nullable().optional(),
  slots: z.array(ReportBlockSlotSchema).optional(),
  blocks: z.array(FrozenBlockSchema),
});

const FrozenCitationSchema = ReportCitationSchema.partial().extend({
  key: z.string(),
  number: z.number().optional(),
});

export const PublishedReportBodySchema = z.object({
  contract_version: z.number().optional(),
  body_version: z.number().optional(),
  captured_at: z.string().optional(),
  version: z.number().optional(),
  cover: ReportCoverSchema.nullable().optional(),
  fit_result: ReportFitSchema,
  tokens: z.record(z.string()),
  /** v2 bodies carry pages; a v1 body carries sections, shown as pages. */
  pages: z.array(FrozenPageSchema).optional(),
  sections: z.array(FrozenPageSchema).optional(),
  tables: ReportTablesSchema.nullable().optional(),
  citations: z.array(FrozenCitationSchema).optional(),
});
export type PublishedReportBody = z.infer<typeof PublishedReportBodySchema>;

/** The frozen body of a published version, from its snapshot. */
export async function getPublishedReportBody(
  engagementId: string,
  snapshotId: string,
): Promise<PublishedReportBody> {
  const snapshot = await getSnapshot(engagementId, snapshotId);
  return PublishedReportBodySchema.parse(snapshot.frozen_state);
}

export function usePublishedReportQuery(engagementId: string, snapshotId: string | null) {
  return useQuery({
    queryKey: [...vendorIntelligenceKeys.all(engagementId), 'report', 'published', snapshotId],
    queryFn: () => getPublishedReportBody(engagementId, snapshotId!),
    enabled: snapshotId !== null,
    staleTime: Infinity,
  });
}

const base = (engagementId: string) =>
  `/api/v1/engagements/${engagementId}/vendor-intelligence/report`;

export const vendorReportKey = (engagementId: string) =>
  [...vendorIntelligenceKeys.all(engagementId), 'report'] as const;

/** How often the read repeats while a published version's PDF is rendering. */
export const PDF_POLL_MS = 3000;

export function getVendorReport(engagementId: string): Promise<VendorReport> {
  return apiClient(base(engagementId), VendorReportSchema);
}

export function draftVendorReport(
  engagementId: string,
  focus: string | null,
): Promise<VendorReport> {
  return apiClient(`${base(engagementId)}:draft`, VendorReportSchema, {
    method: 'POST',
    body: focus ? { focus } : {},
  });
}

export function publishVendorReport(
  engagementId: string,
  idempotencyKey: string,
): Promise<PublishVendorReportResult> {
  return apiClient(`${base(engagementId)}:publish`, PublishResultSchema, {
    method: 'POST',
    body: {},
    idempotencyKey,
  });
}

export function rewriteReportBlock(
  engagementId: string,
  blockId: string,
  request: RewriteBlockRequest,
): Promise<RewriteBlockResult> {
  return apiClient(`${base(engagementId)}/blocks/${blockId}:rewrite`, RewriteResultSchema, {
    method: 'POST',
    body: request,
  });
}

export function saveReportBlock(
  engagementId: string,
  blockId: string,
  request: SaveBlockRequest,
): Promise<SaveBlockResult> {
  return apiClient(`${base(engagementId)}/blocks/${blockId}`, SaveResultSchema, {
    method: 'PATCH',
    body: request,
  });
}

export function revertReportBlock(engagementId: string, blockId: string): Promise<SaveBlockResult> {
  return apiClient(`${base(engagementId)}/blocks/${blockId}:revert`, SaveResultSchema, {
    method: 'POST',
    body: {},
  });
}

/** A key for one publish: made when the person asks, reused if the request is retried. */
export function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `vir-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** True while a version's PDF is still rendering, so the read repeats until it settles. */
export function pdfRendering(report: VendorReport | undefined): boolean {
  return Boolean(
    report?.versions.some(
      (v) =>
        v.pdf === null || (v.pdf.render_status !== 'succeeded' && v.pdf.render_status !== 'failed'),
    ),
  );
}

export function useVendorReportQuery(engagementId: string) {
  return useQuery({
    queryKey: vendorReportKey(engagementId),
    queryFn: () => getVendorReport(engagementId),
    refetchInterval: (query) => (pdfRendering(query.state.data) ? PDF_POLL_MS : false),
  });
}

export function useDraftVendorReport(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (focus: string | null) => draftVendorReport(engagementId, focus),
    onSuccess: (report) => qc.setQueryData(vendorReportKey(engagementId), report),
  });
}

export function usePublishVendorReport(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idempotencyKey: string) => publishVendorReport(engagementId, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vendorReportKey(engagementId) });
      void qc.invalidateQueries({ queryKey: ['snapshots', engagementId] });
    },
  });
}

/** The read with one block replaced, so a saved block shows before the read refreshes. */
export function withBlock(report: VendorReport, block: ReportBlock): VendorReport {
  if (!report.draft) return report;
  const swap = <T extends { blocks: ReportBlock[] }>(group: T): T => ({
    ...group,
    blocks: group.blocks.map((b) => (b.id === block.id ? block : b)),
  });
  return {
    ...report,
    draft: {
      ...report.draft,
      ...(report.draft.sections ? { sections: report.draft.sections.map(swap) } : {}),
      ...(report.draft.pages ? { pages: report.draft.pages.map(swap) } : {}),
    },
  };
}

/** Save and revert: the answered block replaces the cached one, then the read refreshes. */
export function useReportBlockMutations(engagementId: string) {
  const qc = useQueryClient();
  const settle = (result: SaveBlockResult) => {
    qc.setQueryData<VendorReport>(vendorReportKey(engagementId), (old) =>
      old ? withBlock(old, result.block) : old,
    );
    void qc.invalidateQueries({ queryKey: vendorReportKey(engagementId) });
  };
  const save = useMutation({
    mutationFn: ({ blockId, request }: { blockId: string; request: SaveBlockRequest }) =>
      saveReportBlock(engagementId, blockId, request),
    onSuccess: settle,
  });
  const revert = useMutation({
    mutationFn: (blockId: string) => revertReportBlock(engagementId, blockId),
    onSuccess: settle,
  });
  return { save, revert };
}
